import { useMemo } from "react"
import { GeoJsonLayer } from "@deck.gl/layers"
import { PathStyleExtension, PathStyleExtensionProps } from "@deck.gl/extensions"
import { AirspaceFeatureType } from "@nova/constants"
import { useAppSelector } from "integrations/redux/hooks"
import { useMapBackground } from "features/map/control/useMapStyles"
import { selectActivePopup, selectHoveredAirspaces } from "features/overview/slice"
import { useLocalStorageValue } from "tools/store/localStorageRegistry"
import { getDashArray, getFillColor, getLineColor, getLineWidth } from "./featureColors"
import { useAirspaceClickHandler } from "./useAirspaceClickHandler"
import { useAirspaceHoverHandler } from "./useAirspaceHoverHandler"

export const useAirspaceLayer = ({
    type,
    visibleFeatures,
    enabled,
}: {
    type: AirspaceFeatureType
    visibleFeatures: GeoJSON.Feature[]
    enabled: boolean
}) => {
    const airspacesEnabled = useLocalStorageValue("airspaceFilters").enabled
    const { isLight } = useMapBackground("overview")
    const handleClick = useAirspaceClickHandler(type)
    const handleHover = useAirspaceHoverHandler()

    const hoveredAirspaces = useAppSelector(selectHoveredAirspaces)
    const hoveredIds = useMemo(
        () =>
            new Set(
                hoveredAirspaces
                    .filter((hovered) => hovered.type === type)
                    .map((hovered) => hovered.id)
            ),
        [hoveredAirspaces, type]
    )

    const activePopup = useAppSelector(selectActivePopup)
    const activeId = activePopup?.type === "airspace" ? activePopup.id : undefined
    const listIds = useMemo(
        () =>
            activePopup?.type === "airspaceFeatureList"
                ? new Set(
                      activePopup.features
                          .filter((feature) => feature.type === type)
                          .map((feature) => feature.id)
                  )
                : undefined,
        [activePopup, type]
    )

    return useMemo(
        () =>
            new GeoJsonLayer<GeoJSON.GeoJsonProperties, PathStyleExtensionProps>({
                id: type,
                data: {
                    type: "FeatureCollection",
                    features: airspacesEnabled && enabled ? visibleFeatures : [],
                },
                filled: true,
                stroked: true,
                getFillColor: getFillColor({ hoveredIds, activeId, listIds, featureType: type }),
                getLineColor: getLineColor({
                    hoveredIds,
                    activeId,
                    listIds,
                    featureType: type,
                    isLight,
                }),
                getDashArray: getDashArray({ hoveredIds, activeId, listIds }),
                dashJustified: false,
                extensions: [new PathStyleExtension({ dash: true })],
                updateTriggers: {
                    getFillColor: [hoveredIds, activeId, listIds],
                    getLineColor: [hoveredIds, activeId, listIds, isLight],
                    getDashArray: [hoveredIds, activeId, listIds],
                    getLineWidth: [hoveredIds, activeId, listIds],
                },
                lineWidthUnits: "pixels",
                getLineWidth: getLineWidth({ hoveredIds, activeId, listIds }),
                pickable: true,
                onClick: handleClick,
                onHover: handleHover,
                parameters: {
                    depthCompare: "always",
                },
            }),
        [
            type,
            airspacesEnabled,
            enabled,
            visibleFeatures,
            hoveredIds,
            activeId,
            listIds,
            isLight,
            handleClick,
            handleHover,
        ]
    )
}
